import { Row, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  useCustomerBenefits,
  useUpdateBenefitSubscription,
} from '../../hooks/useCustomerBenefits';
import {
  Benefit,
  BenefitSubscription,
  CustomerData,
} from '../../types/customer';
import { ErrorState } from '../ErrorState';
import { BENEFIT_LOGOS } from './benefitLogos';
import { BenefitStatusModal } from './BenefitStatusModal';
import { BenefitSubscriptionsTable } from './BenefitSubscriptionsTable';

const { Title } = Typography;

type CustomerBenefitsTabProps = {
  enabled: boolean;
  customerData: CustomerData;
};

type SelectedSubscription = {
  benefit: Benefit;
  subscription: BenefitSubscription;
};

export const CustomerBenefitsTab: React.FC<CustomerBenefitsTabProps> = ({
  enabled,
  customerData,
}) => {
  const { data, isLoading, isError, refetch } = useCustomerBenefits(
    enabled,
    customerData.customer.id
  );
  const updateBenefitSubscription = useUpdateBenefitSubscription();
  const benefits = data?.benefits ?? [];
  const benefitsSignature = benefits.map(benefit => benefit.slug).join('|');
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [selectedSubscription, setSelectedSubscription] =
    useState<SelectedSubscription | null>(null);

  useEffect(() => {
    setExpandedRowKeys(benefits.length === 1 ? [benefits[0].slug] : []);
  }, [benefitsSignature]);

  const columns = useMemo<ColumnsType<Benefit>>(
    () => [
      {
        title: 'Nome do benefício',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, benefit) => (
          <Space>
            {BENEFIT_LOGOS[benefit.slug]}
            <span>{name}</span>
          </Space>
        ),
      },
      {
        title: 'Assinaturas aptas',
        dataIndex: 'subscriptions',
        key: 'eligibleSubscriptions',
        render: (subscriptions: BenefitSubscription[]) => subscriptions.length,
      },
      {
        title: 'Assinaturas ativas',
        dataIndex: 'subscriptions',
        key: 'activeSubscriptions',
        render: (subscriptions: BenefitSubscription[]) =>
          subscriptions.filter(subscription => subscription.isActiveBenefit)
            .length,
      },
    ],
    []
  );

  const handleConfirm = async () => {
    if (!selectedSubscription) return;

    const { benefit, subscription } = selectedSubscription;

    try {
      await updateBenefitSubscription.mutateAsync({
        slug: benefit.slug,
        subscriptionId: subscription.id,
        isActiveBenefit: !subscription.isActiveBenefit,
        customerId: customerData.customer.id,
      });
      setSelectedSubscription(null);
    } catch {
      // O hook exibe o feedback e o modal permanece aberto para nova tentativa.
    }
  };

  if (isError) {
    return (
      <TabContainer>
        <Title level={5}>Benefícios</Title>
        <ErrorState
          title='Não foi possível carregar os benefícios.'
          subtitle='Tente novamente em alguns instantes.'
          onClickButton={() => refetch()}
        />
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      <Title level={5}>Benefícios</Title>
      <TableWrapper>
        <Table
          columns={columns}
          dataSource={benefits}
          rowKey='slug'
          loading={isLoading}
          pagination={false}
          locale={{ emptyText: 'Não há dados' }}
          expandable={{
            expandedRowKeys,
            onExpandedRowsChange: keys => setExpandedRowKeys(Array.from(keys)),
            expandedRowRender: benefit => (
              <BenefitSubscriptionsTable
                benefit={benefit}
                onSelectSubscription={subscription =>
                  setSelectedSubscription({ benefit, subscription })
                }
              />
            ),
          }}
        />
      </TableWrapper>

      <BenefitStatusModal
        selectedSubscription={selectedSubscription}
        isLoading={updateBenefitSubscription.isPending}
        onCancel={() => setSelectedSubscription(null)}
        onConfirm={handleConfirm}
      />
    </TabContainer>
  );
};

const TabContainer = styled(Space).attrs({
  direction: 'vertical',
  size: 'large',
})`
  display: flex;
  margin-top: 24px;

  .ant-typography {
    margin: 0;
  }
`;

const TableWrapper = styled(Row)`
  width: 100%;
  overflow-x: auto;
  padding-bottom: 20px;

  > .ant-table-wrapper {
    width: 100%;
    min-width: 720px;
    border-top: 1px solid #303030;
    border-left: 1px solid #303030;
    border-right: 1px solid #303030;
  }
`;
